// The Design canvas: tiled document view (D1), overlay for tools and the selection, rulers in document
// units, pan (wheel, Space-drag, middle-drag, Hand tool) and zoom (Ctrl/⌘ + wheel around the cursor, Zoom
// tool, Ctrl+0 fit, Ctrl+1 100%). Pointer events go to the active tool.
import { useEffect, useRef } from 'react';
import { useDesign, type View } from '../state';
import { clampZoom, fitView, rulerStep, toScreen, toWorld, zoomAt } from '../render/viewMath';
import { fromPt } from '../model/units';
import { drawOverlay } from '../render/scene';
import { currentList, renderer } from '../render/instance';
import { useDesignUi } from './designUi';
import { toolFor } from '../tools/registry';
import { addGuide, moveGuide, removeGuide } from '../commands/commands';
import type { ToolHost, ToolPointer } from '../tools/tool';
import s from './Design.module.css';

export const RULER = 20;

export { renderer };

/** The canvas element and its size (tests, tools, export of the view). */
export const canvasHooks: {
  el: HTMLCanvasElement | null;
  size: { w: number; h: number };
  lastFrameMs: number;
} = {
  el: null,
  size: { w: 0, h: 0 },
  lastFrameMs: 0,
};

const host = (redraw: () => void, setCursor: (c: string) => void): ToolHost => ({
  list: renderer.list,
  view: () => useDesign.getState().view,
  redrawOverlay: redraw,
  setCursor,
});

export function DesignCanvas() {
  const wrap = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLCanvasElement>(null);
  const over = useRef<HTMLCanvasElement>(null);
  const top = useRef<HTMLCanvasElement>(null);
  const left = useRef<HTMLCanvasElement>(null);
  const version = useDesign((st) => st.version);
  const tool = useDesignUi((st) => st.tool);
  const selection = useDesignUi((st) => st.selection);
  const viewOpts = useDesignUi(
    (st) => `${st.showGrid}|${st.showGuides}|${st.snap.gridSpacing}|${st.keyObject}`,
  );

  const overlayNow = () => {
    const { doc, view } = useDesign.getState();
    if (!doc || !over.current || view.zoom === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const t = toolFor(useDesignUi.getState().tool);
    const h = host(overlayNow, (c) => over.current && (over.current.style.cursor = c));
    const ui = useDesignUi.getState();
    drawOverlay(
      over.current.getContext('2d')!,
      doc,
      view,
      dpr,
      t?.overlay ? { draw: (g, v) => t.overlay!(g, v, h) } : null,
      {
        grid: ui.showGrid,
        gridSpacing: ui.snap.gridSpacing,
        guides: ui.showGuides,
      },
    );
  };

  // Size the canvases to the container (device pixels for sharpness).
  useEffect(() => {
    const el = wrap.current!;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth - RULER,
        h = el.clientHeight - RULER;
      const dpr = window.devicePixelRatio || 1;
      for (const [c, cw, ch] of [
        [main.current!, w, h],
        [over.current!, w, h],
        [top.current!, w, RULER],
        [left.current!, RULER, h],
      ] as const) {
        c.width = Math.max(1, Math.round(cw * dpr));
        c.height = Math.max(1, Math.round(ch * dpr));
        c.style.width = `${cw}px`;
        c.style.height = `${ch}px`;
      }
      canvasHooks.size = { w, h };
      const st = useDesign.getState();
      if (st.doc && st.view.zoom === 0) st.setView(fitView(st.doc.artboards, w, h));
      else useDesign.setState({ version: st.version + 1 });
    });
    ro.observe(el);
    canvasHooks.el = main.current;
    return () => {
      ro.disconnect();
      canvasHooks.el = null;
    };
  }, []);

  // Fit a newly opened document; drop a selection that belongs to another document.
  const docId = useDesign((st) => st.doc?.id);
  useEffect(() => {
    const st = useDesign.getState();
    useDesignUi.getState().set({ selection: [] });
    if (st.doc && canvasHooks.size.w > 0)
      st.setView(fitView(st.doc.artboards, canvasHooks.size.w, canvasHooks.size.h));
  }, [docId]);

  // Draw on every change (coalesced into one animation frame). While the zoom settles, draw again.
  useEffect(() => {
    let id = requestAnimationFrame(function frame() {
      const { doc, view } = useDesign.getState();
      if (!doc || !main.current || view.zoom === 0) return;
      const dpr = window.devicePixelRatio || 1;
      const t0 = performance.now();
      renderer.setDoc(doc);
      const again = renderer.render(main.current.getContext('2d')!, view, dpr);
      overlayNow();
      drawRulers(top.current!, left.current!, view, doc.units, doc.rasterDpi, dpr);
      canvasHooks.lastFrameMs = performance.now() - t0;
      if (again) id = requestAnimationFrame(frame);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, selection, tool, viewOpts]);

  // Pointer input: pan/zoom first, otherwise the active tool.
  useEffect(() => {
    const el = over.current!;
    let space = false;
    let pan: { x: number; y: number; v: View } | null = null;
    let down = false;
    const toPointer = (e: PointerEvent | MouseEvent): ToolPointer => {
      const r = el.getBoundingClientRect();
      const v = useDesign.getState().view;
      const sx = e.clientX - r.left,
        sy = e.clientY - r.top;
      const [wx, wy] = toWorld(v, sx, sy);
      return {
        sx,
        sy,
        wx,
        wy,
        shift: e.shiftKey,
        alt: e.altKey,
        mod: e.ctrlKey || e.metaKey,
        button: e.button,
        px: 1 / v.zoom,
      };
    };
    const h = host(overlayNow, (c) => (el.style.cursor = c));
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const st = useDesign.getState();
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey)
        st.setView(zoomAt(st.view, e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0015)));
      else st.setView({ panX: st.view.panX - e.deltaX, panY: st.view.panY - e.deltaY });
    };
    const onDown = (e: PointerEvent) => {
      const toolId = useDesignUi.getState().tool;
      el.setPointerCapture(e.pointerId);
      if (e.button === 0 && toolId === 'zoom' && !space) {
        const st = useDesign.getState();
        const r = el.getBoundingClientRect();
        st.setView(zoomAt(st.view, e.clientX - r.left, e.clientY - r.top, e.altKey ? 0.5 : 2));
        return;
      }
      if (e.button === 1 || (e.button === 0 && (space || toolId === 'hand'))) {
        pan = { x: e.clientX, y: e.clientY, v: useDesign.getState().view };
        e.preventDefault();
        return;
      }
      const t = toolFor(toolId);
      if (t && e.button === 0) {
        currentList();
        down = true;
        t.down(toPointer(e), h);
        overlayNow();
      }
    };
    const onMove = (e: PointerEvent) => {
      if (pan) {
        useDesign
          .getState()
          .setView({ panX: pan.v.panX + e.clientX - pan.x, panY: pan.v.panY + e.clientY - pan.y });
        return;
      }
      currentList(); // the tool may read objects created or changed since the last frame
      toolFor(useDesignUi.getState().tool)?.move(toPointer(e), h, down);
    };
    const onUp = (e: PointerEvent) => {
      if (pan) {
        pan = null;
        return;
      }
      if (down) {
        down = false;
        currentList();
        toolFor(useDesignUi.getState().tool)?.up(toPointer(e), h);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ') space = e.type === 'keydown';
    };
    // Dragging out of a ruler makes a guide (top ruler → horizontal, left ruler → vertical); dropping it back on
    // the ruler cancels it.
    const rulerDrag = (axis: 'x' | 'y') => (e: PointerEvent) => {
      const st = useDesign.getState();
      if (!st.doc) return;
      const out: string[] = [];
      const r = el.getBoundingClientRect();
      const pos = (ev: PointerEvent) => {
        const v = useDesign.getState().view;
        return axis === 'y'
          ? (ev.clientY - r.top - v.panY) / v.zoom
          : (ev.clientX - r.left - v.panX) / v.zoom;
      };
      const key = `guide-${Date.now()}`;
      st.run({ ...addGuide(axis, pos(e), out), merge: key });
      const id = out[0]!;
      const move = (ev: PointerEvent) => useDesign.getState().run(moveGuide(id, pos(ev), key));
      const up = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        const onRuler = axis === 'y' ? ev.clientY < r.top : ev.clientX < r.left;
        if (onRuler) useDesign.getState().run(removeGuide(id, key));
        useDesign.getState().endGesture();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      e.preventDefault();
    };
    const topR = top.current!,
      leftR = left.current!;
    const onTop = rulerDrag('y'),
      onLeft = rulerDrag('x');
    topR.addEventListener('pointerdown', onTop);
    leftR.addEventListener('pointerdown', onLeft);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      topR.removeEventListener('pointerdown', onTop);
      leftR.removeEventListener('pointerdown', onLeft);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrap} className={s.canvasWrap} data-testid="design-canvas">
      <div className={s.rulerCorner} />
      <canvas ref={top} className={s.rulerTop} data-testid="design-ruler-top" />
      <canvas ref={left} className={s.rulerLeft} data-testid="design-ruler-left" />
      <canvas ref={main} className={s.main} data-testid="design-main" />
      <canvas ref={over} className={s.overlay} data-testid="design-overlay" />
    </div>
  );
}

export function fitToWindow() {
  const st = useDesign.getState();
  // Before the canvas has a size (just mounted), the resize observer fits it instead.
  if (canvasHooks.size.w <= 0 || canvasHooks.size.h <= 0) return;
  if (st.doc) st.setView(fitView(st.doc.artboards, canvasHooks.size.w, canvasHooks.size.h));
}
export function zoomTo(z: number) {
  const st = useDesign.getState();
  const { w, h } = canvasHooks.size;
  st.setView(zoomAt(st.view, w / 2, h / 2, clampZoom(z) / st.view.zoom));
}

function drawRulers(
  top: HTMLCanvasElement,
  left: HTMLCanvasElement,
  v: View,
  unit: Parameters<typeof fromPt>[1],
  dpi: number,
  dpr: number,
) {
  const unitPerPt = fromPt(1, unit, dpi);
  const step = rulerStep(unitPerPt, v.zoom);
  for (const [c, horizontal] of [
    [top, true],
    [left, false],
  ] as const) {
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const len = (horizontal ? c.width : c.height) / dpr;
    g.fillStyle = '#2a2a2a';
    g.fillRect(0, 0, c.width / dpr, c.height / dpr);
    g.strokeStyle = '#777';
    g.fillStyle = '#aaa';
    g.font = '9px system-ui, sans-serif';
    g.lineWidth = 1;
    const at0 = toWorld(v, 0, 0),
      at1 = toWorld(v, len, len);
    const w0 = horizontal ? at0[0] : at0[1],
      w1 = horizontal ? at1[0] : at1[1];
    const u0 = Math.floor((w0 * unitPerPt) / step) * step;
    for (let u = u0; u <= w1 * unitPerPt + step; u += step / 5) {
      const pt = u / unitPerPt;
      const p = horizontal ? toScreen(v, pt, 0)[0] : toScreen(v, 0, pt)[1];
      const major = Math.abs(u / step - Math.round(u / step)) < 1e-6;
      g.beginPath();
      if (horizontal) {
        g.moveTo(Math.round(p) + 0.5, RULER);
        g.lineTo(Math.round(p) + 0.5, major ? 4 : 14);
      } else {
        g.moveTo(RULER, Math.round(p) + 0.5);
        g.lineTo(major ? 4 : 14, Math.round(p) + 0.5);
      }
      g.stroke();
      if (major) {
        const label = String(Number(u.toFixed(4)));
        if (horizontal) g.fillText(label, p + 2, 10);
        else {
          g.save();
          g.translate(10, p + 2);
          g.rotate(-Math.PI / 2);
          g.fillText(label, -g.measureText(label).width - 2, 0);
          g.restore();
        }
      }
    }
  }
}
